// ─────────────────────────────────────────────────────────────────────────────
// LCCA NEXUS — Lab Guide Page
// LCCA concepts, formulas, standards, deployment instructions
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { Book, Code2, Cloud, Cpu, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardBody, Badge } from '@/components/ui/index';
import { cn } from '@/lib/utils';

// ── Accordion ─────────────────────────────────────────────────────────────────
function Accordion({ title, icon, badge, children }: {
  title: string; icon: React.ReactNode; badge?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden">
      <button
        className="flex items-center gap-3 w-full px-5 py-4 text-left hover:bg-parchment-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="w-8 h-8 rounded-lg bg-navy-50 flex items-center justify-center text-navy-600 flex-shrink-0">
          {icon}
        </div>
        <span className="font-display font-bold text-navy-900 text-sm flex-1">{title}</span>
        {badge && <Badge variant="amber">{badge}</Badge>}
        {open ? <ChevronUp size={16} className="text-navy-400" /> : <ChevronDown size={16} className="text-navy-400" />}
      </button>
      {open && (
        <div className="border-t border-parchment-200">
          <CardBody className="prose-sm">{children}</CardBody>
        </div>
      )}
    </Card>
  );
}

// ── Code block ────────────────────────────────────────────────────────────────
function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-navy-900 text-green-300 rounded-lg p-4 text-xs font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap">
      {children}
    </pre>
  );
}

// ── Step card ─────────────────────────────────────────────────────────────────
function Step({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-navy-700 text-white font-display font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
        {num}
      </div>
      <div className="flex-1 pb-6 border-b border-parchment-100 last:border-b-0">
        <p className="font-display font-bold text-navy-900 text-sm mb-2">{title}</p>
        <div className="text-sm text-navy-600 leading-relaxed flex flex-col gap-3">{children}</div>
      </div>
    </div>
  );
}

// ── Guide page ────────────────────────────────────────────────────────────────
export function Guide() {
  return (
    <div className="flex flex-col gap-5 max-w-4xl">

      {/* Header */}
      <div className="bg-navy-900 rounded-xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.1) 1px,transparent 1px)',
          backgroundSize: '32px 32px',
        }} />
        <div className="relative z-10">
          <Badge variant="amber" className="mb-4">Lab Guide</Badge>
          <h2 className="font-display font-bold text-3xl text-white mb-3 leading-tight">
            LCCA Nexus — Student Guide
          </h2>
          <p className="text-navy-300 text-base leading-relaxed max-w-2xl">
            Your complete reference for the Life-Cycle Cost Analysis lab exercise in{' '}
            <em>Smart Technologies for Facility Management</em>, Bachelor of Civil Engineering.
          </p>
        </div>
      </div>

      {/* LCCA Theory */}
      <Accordion title="What is Life-Cycle Cost Analysis?" icon={<Book size={16} />} badge="Theory">
        <div className="flex flex-col gap-4">
          <p className="text-navy-700 leading-relaxed">
            <strong>Life-Cycle Cost Analysis (LCCA)</strong> is an economic evaluation method used to assess
            the total cost of ownership of a building system or asset over its entire useful life.
            Unlike simple first-cost comparisons, LCCA accounts for all future costs — discounted back to
            a common point in time (present value) — enabling evidence-based decisions between competing alternatives.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: 'Initial Cost (IC)',       desc: 'One-time upfront investment at Year 0. Already in present-value terms.' },
              { title: 'O&M Costs',               desc: 'Annual recurring operation and maintenance costs, escalated over time.' },
              { title: 'Energy Costs',            desc: 'Annual utility bills. Escalated at a higher rate than general inflation.' },
              { title: 'Replacement Costs',       desc: 'Major capital expenditures at specific future years, discounted to PV.' },
              { title: 'Residual Value',          desc: 'Salvage/market value at end of study. Subtracted as a benefit.' },
              { title: 'Discount Rate (d)',        desc: 'Converts future money to present value; reflects time value of money.' },
            ].map(c => (
              <div key={c.title} className="bg-parchment-50 rounded-lg p-3 border border-parchment-200">
                <p className="font-semibold text-navy-800 text-sm mb-1">{c.title}</p>
                <p className="text-navy-600 text-xs leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* Formulas */}
      <Accordion title="Key LCCA Formulas" icon={<Cpu size={16} />} badge="Formulas">
        <div className="flex flex-col gap-4">
          {[
            {
              name: 'Discount Factor (DF)',
              eq: 'DF(t) = (1 + d)⁻ᵗ',
              desc: 'Converts a future amount at year t to its present value equivalent.',
            },
            {
              name: 'Present Worth of Single Cost',
              eq: 'PW = F × (1 + d)⁻ᵗ',
              desc: 'Used for one-time future costs like replacements. F = future cost, t = year.',
            },
            {
              name: 'Present Worth of Escalating Annual Cost',
              eq: 'PW = Σ [Cₜ × (1+e)ᵗ⁻¹ × (1+d)⁻ᵗ]   for t = 1 to N',
              desc: "Σ sums each year's escalated cost back to present value. e = escalation rate.",
            },
            {
              name: 'Uniform Annual Cost (UAC)',
              eq: 'CRF = d ÷ [1 − (1+d)⁻ᴺ]\nUAC = LCC × CRF',
              desc: 'Converts a lump-sum LCC to an equivalent uniform annual payment. CRF = Capital Recovery Factor.',
            },
            {
              name: 'Total LCC (Net Present Value)',
              eq: 'LCC = IC + PW(O&M) + PW(Energy) + PW(Replacements) − PW(Residual Value)',
              desc: 'The sum of all present-worth costs over the study period. Lower LCC = more cost-effective.',
            },
          ].map(f => (
            <div key={f.name} className="border border-parchment-200 rounded-lg overflow-hidden">
              <div className="bg-navy-900 px-4 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-navy-400 mb-1">{f.name}</p>
                <p className="font-mono text-amber-300 text-sm leading-relaxed whitespace-pre">{f.eq}</p>
              </div>
              <div className="px-4 py-2.5 bg-parchment-50">
                <p className="text-xs text-navy-600 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Accordion>

      {/* Standards */}
      <Accordion title="Standards & References" icon={<Book size={16} />}>
        <div className="overflow-x-auto rounded-lg border border-parchment-200">
          <table className="data-table text-xs">
            <thead><tr><th className="!text-left">Standard</th><th className="!text-left">Title</th><th>Scope</th></tr></thead>
            <tbody>
              {[
                { std: 'ISO 15686-5:2017',  title: 'Buildings — Service life planning', scope: 'Life-cycle costing framework' },
                { std: 'ASTM E917',         title: 'Standard Practice for Measuring LCC', scope: 'Buildings & systems' },
                { std: 'NIST HB 135',       title: 'Life-Cycle Costing Manual (FEMP)',    scope: 'Federal energy programs' },
                { std: 'ASHRAE Guideline 14', title: 'Measurement of Energy & Water Savings', scope: 'Building efficiency' },
                { std: 'ISO 14040',         title: 'Environmental LCA',                   scope: 'Environmental impacts' },
              ].map(r => (
                <tr key={r.std}>
                  <td className="!font-mono !text-navy-700">{r.std}</td>
                  <td className="!font-body !text-left">{r.title}</td>
                  <td className="!font-body">{r.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

      {/* Using the App */}
      <Accordion title="How to Use LCCA Nexus" icon={<Cpu size={16} />} badge="Tutorial">
        <div className="flex flex-col gap-0">
          <Step num="1" title="Create a Project">
            <p>Click <strong>New Project</strong> on the Dashboard. Fill in your name, student ID, course section, and instructor. Select the asset type (e.g., HVAC, Electrical).</p>
          </Step>
          <Step num="2" title="Enter Parameters in the Calculator">
            <p>In the Calculator tab, enter all input parameters:</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-navy-600">
              <li>Analysis period (e.g., 25 years) and discount rate (e.g., 5%)</li>
              <li>Initial capital cost and residual/salvage value</li>
              <li>Annual O&M cost with escalation rate</li>
              <li>Annual energy cost with energy escalation rate</li>
              <li>Any planned replacement events (name, cost, year)</li>
            </ul>
          </Step>
          <Step num="3" title="Click Calculate LCCA">
            <p>The app instantly computes Total LCC, UAC, and all year-by-year cash flows. Results appear with interactive charts on the right side.</p>
          </Step>
          <Step num="4" title="Save as Scenario">
            <p>Click <strong>Save as Scenario</strong> and give it a name (e.g., "Alternative A — Standard System"). The first saved scenario is automatically marked as the Baseline.</p>
          </Step>
          <Step num="5" title="Compare Alternatives">
            <p>Repeat steps 2–4 for each design alternative. Then go to the <strong>Compare</strong> page to see a ranked side-by-side comparison with charts.</p>
          </Step>
          <Step num="6" title="Generate Lab Report">
            <p>In the <strong>Lab Report</strong> tab, click <strong>Print / Save PDF</strong> to generate your structured report for submission.</p>
          </Step>
        </div>
      </Accordion>

      {/* Cloudflare Deployment */}
      <Accordion title="Deploy on Cloudflare (Free Tier)" icon={<Cloud size={16} />} badge="Deployment">
        <div className="flex flex-col gap-4">
          <p className="text-navy-700 text-sm">Deploy this full-stack app free on Cloudflare — Workers (API) + Pages (Frontend) + D1 (Database).</p>

          <Step num="1" title="Install Wrangler CLI">
            <CodeBlock>{`npm install -g wrangler
wrangler login    # Opens browser to authenticate your Cloudflare account`}</CodeBlock>
          </Step>

          <Step num="2" title="Create the D1 Database">
            <CodeBlock>{`wrangler d1 create lcca-nexus-db
# Copy the database_id from the output and paste it into server/wrangler.toml`}</CodeBlock>
          </Step>

          <Step num="3" title="Run Database Migrations">
            <CodeBlock>{`# Apply schema locally first (for testing)
wrangler d1 migrations apply lcca-nexus-db --local

# Then apply to production D1
wrangler d1 migrations apply lcca-nexus-db --remote`}</CodeBlock>
          </Step>

          <Step num="4" title="Deploy the API Worker">
            <CodeBlock>{`cd server
wrangler deploy
# Your API is live at: https://lcca-nexus-api.YOUR_SUBDOMAIN.workers.dev`}</CodeBlock>
          </Step>

          <Step num="5" title="Build & Deploy the Frontend">
            <CodeBlock>{`cd client
# Set your API URL in environment
echo "VITE_API_URL=https://lcca-nexus-api.YOUR_SUBDOMAIN.workers.dev" > .env.production

npm run build    # Outputs to client/dist/

# Deploy to Cloudflare Pages
wrangler pages deploy dist --project-name lcca-nexus
# Your app is live at: https://lcca-nexus.pages.dev`}</CodeBlock>
          </Step>

          <Step num="6" title="Update CORS Origin">
            <p>In <code className="text-xs bg-parchment-200 px-1 py-0.5 rounded">server/wrangler.toml</code>, set:</p>
            <CodeBlock>{`[vars]
CORS_ORIGIN = "https://lcca-nexus.pages.dev"  # Your Pages URL`}</CodeBlock>
            <p>Then redeploy the worker: <code className="text-xs bg-parchment-200 px-1 py-0.5 rounded">wrangler deploy</code></p>
          </Step>
        </div>
      </Accordion>

      {/* Scalable Deployment */}
      <Accordion title="Scale Beyond Cloudflare (Railway / Render / VPS)" icon={<Code2 size={16} />} badge="Advanced">
        <div className="flex flex-col gap-4">
          <p className="text-navy-700 text-sm leading-relaxed">
            When you need persistent file storage, PostgreSQL, WebSockets, or higher throughput,
            migrate to a Node.js platform using the included <code className="text-xs bg-parchment-200 px-1 py-0.5 rounded">server/src/node-server.ts</code> adapter.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { platform: 'Railway',  url: 'railway.app',  note: 'Easiest — connect GitHub, auto-deploy' },
              { platform: 'Render',   url: 'render.com',   note: 'Free tier, PostgreSQL add-on available' },
              { platform: 'Fly.io',   url: 'fly.io',       note: 'Edge deployment, close to Cloudflare' },
            ].map(p => (
              <a key={p.platform} href={`https://${p.url}`} target="_blank" rel="noopener noreferrer"
                className="block bg-parchment-50 rounded-lg p-3 border border-parchment-200 hover:border-navy-400 transition-colors group">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm text-navy-900">{p.platform}</span>
                  <ExternalLink size={11} className="text-navy-400 group-hover:text-navy-700" />
                </div>
                <p className="text-xs text-navy-500">{p.note}</p>
              </a>
            ))}
          </div>

          <Step num="1" title="Switch to PostgreSQL (optional but recommended)">
            <CodeBlock>{`npm install drizzle-orm postgres
# Replace drizzle-orm/d1 imports with drizzle-orm/postgres-js
# Update connection string from DATABASE_URL env variable`}</CodeBlock>
          </Step>

          <Step num="2" title="Run the Node.js Server">
            <CodeBlock>{`# Install dependencies
npm install

# Development
PORT=3001 npx tsx server/src/node-server.ts

# Production (build first)
npm run build --workspace=server
node server/dist/node-server.js`}</CodeBlock>
          </Step>

          <Step num="3" title="Environment Variables for Production">
            <CodeBlock>{`PORT=3001
NODE_ENV=production
CORS_ORIGIN=https://your-frontend-domain.com
DATABASE_URL=postgresql://user:password@host:5432/lcca_nexus  # if using PostgreSQL`}</CodeBlock>
          </Step>

          <Step num="4" title="Deploy Frontend Separately">
            <p>The React frontend is a static build in <code className="text-xs bg-parchment-200 px-1 py-0.5 rounded">client/dist/</code> and can be served by:</p>
            <ul className="list-disc list-inside text-xs text-navy-600 space-y-1">
              <li>Cloudflare Pages (keep on free tier)</li>
              <li>Vercel or Netlify (GitHub auto-deploy)</li>
              <li>Nginx / Apache on any VPS</li>
              <li>The same Node.js server (serve static files via Hono)</li>
            </ul>
          </Step>
        </div>
      </Accordion>

    </div>
  );
}
